
import React, { useCallback, useRef, useState } from 'react';
import { useToast } from '@/hooks/use-toast';
import { DanfeResponse, requestDanfePdf } from '@/services/orderApiService';

export function useDanfe() {
  const [danfeData, setDanfeData] = useState<DanfeResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [currentOrderId, setCurrentOrderId] = useState<number | null>(null);
  const { toast } = useToast();

  const inFlightRef = useRef(false);
  const lastRequestedIdRef = useRef<number | null>(null);

  const fetchDanfe = useCallback(async (id: number) => {
    if (!id) return;

    // Evita disparos repetidos (ex.: loop de useEffect)
    if (inFlightRef.current && lastRequestedIdRef.current === id) return;

    inFlightRef.current = true;
    lastRequestedIdRef.current = id;

    setIsLoading(true);
    setError(null);
    setCurrentOrderId(id);
    setOpen(true);
    
    try {
      const response = await requestDanfePdf(id);
      setDanfeData(response);
      
      if (!response.success) {
        setError(response.mensagem || 'Não foi possível obter o DANFE');
        toast({
          title: 'Erro ao obter DANFE',
          description: response.mensagem || 'Não foi possível obter o documento fiscal',
          variant: 'destructive'
        });
      } else if (!response.danfe) {
        setError('Documento DANFE não disponível');
        toast({
          title: 'DANFE não disponível',
          description: 'O documento fiscal não foi encontrado',
          variant: 'destructive'
        });
      } else {
        toast({
          title: 'DANFE disponível',
          description: 'O documento fiscal foi carregado com sucesso'
        });
      }
    } catch (error) {
      console.error('Error fetching DANFE:', error);
      const errorMessage = error instanceof Error ? error.message : 'Erro ao obter DANFE';
      setError(errorMessage);
      toast({
        title: 'Erro ao obter DANFE',
        description: errorMessage,
        variant: 'destructive'
      });
    } finally {
      inFlightRef.current = false;
      setIsLoading(false);
    }
  }, [toast]);

  const closeDanfe = () => {
    setOpen(false);
    setCurrentOrderId(null);
  };

  return {
    danfeData,
    isLoading,
    error,
    open,
    currentOrderId,
    fetchDanfe,
    closeDanfe,
    setOpen
  };
}
